import { useEffect, useRef, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  downloadChatAttachment,
  type ChatAttachment,
  type ChatMessage,
  type ChatRoom,
} from '@/api/chat.api';
import { useAuth } from '@/hooks/use-auth';
import { formatDate } from '@/lib/utils';
import { User, Check, CheckCheck, Download, FileText } from 'lucide-react';
import { isChatMessageRead } from '@/components/chat-widget/chat-read';

function ChatAttachmentItem({ attachment }: { attachment: ChatAttachment }) {
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const isImage = attachment.mime_type.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let active = true;
    let objectUrl = '';
    void downloadChatAttachment(attachment.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, isImage]);

  const download = async () => {
    setLoading(true);
    try {
      const blob = await downloadChatAttachment(attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name;
      link.click();
      URL.revokeObjectURL(url);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={loading}
      className="mt-2 flex w-full max-w-72 items-center gap-2 overflow-hidden rounded-xl border border-current/15 bg-background/90 p-2 text-left text-foreground shadow-sm disabled:opacity-60"
    >
      {previewUrl ? (
        <img src={previewUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold">{attachment.file_name}</span>
        <span
          className={cn('block text-[10px]', failed ? 'text-destructive' : 'text-muted-foreground')}
        >
          {failed
            ? 'Download unavailable'
            : attachment.file_size < 1024 * 1024
              ? `${Math.max(1, Math.ceil(attachment.file_size / 1024))} KB`
              : `${(attachment.file_size / (1024 * 1024)).toFixed(1)} MB`}
        </span>
      </span>
      <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

interface ChatBubblesProps {
  messages: ChatMessage[];
  loading?: boolean;
  users: { id: string; name: string }[];
  room?: ChatRoom;
}

export function ChatBubbles({ messages, loading, users, room }: ChatBubblesProps) {
  const { user } = useAuth();
  const bottomRef = useRef<HTMLDivElement>(null);

  function resolveUserName(id?: string): string {
    if (!id) return 'User';
    return users.find((u) => u.id === id)?.name ?? 'User';
  }

  function resolveInitial(id?: string): string {
    return resolveUserName(id).trim().slice(0, 1).toUpperCase() || 'U';
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Display in chronological order (API returns newest first)
  const sorted = [...messages].reverse();

  return (
    <ScrollArea className="flex-1 px-4 md:px-8 py-2 w-full bg-background/30 backdrop-blur-sm">
      <div className="space-y-6 py-4 flex flex-col justify-end min-h-full">
        {loading && (
          <div className="text-center text-sm text-muted-foreground py-2 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
            Loading messages...
          </div>
        )}
        {!loading && sorted.length === 0 && (
          <div className="text-center flex flex-col items-center justify-center text-muted-foreground py-12 mt-auto">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <User className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">It's quiet here...</h3>
            <p className="text-sm">Send a message to start the conversation!</p>
          </div>
        )}

        {sorted.map((msg, index) => {
          const isMine = msg.sender_id === user?.id;
          const showAvatar =
            !isMine && (index === 0 || sorted[index - 1]?.sender_id !== msg.sender_id);
          const isRead = isChatMessageRead(msg, room, user?.id);

          return (
            <div
              key={msg.id}
              className={cn(
                'flex w-full items-end gap-2',
                isMine ? 'justify-end' : 'justify-start',
              )}
            >
              {/* Profile Avatar for incoming messages */}
              {!isMine && (
                <div className="flex-shrink-0 w-8 flex justify-center">
                  {showAvatar && (
                    <div className="h-8 w-8 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground text-xs font-bold shadow-glass-sm">
                      {resolveInitial(msg.sender_id)}
                    </div>
                  )}
                </div>
              )}

              <div
                className={cn(
                  'max-w-[75%] lg:max-w-[60%] flex flex-col relative group transition-all duration-200',
                  isMine ? 'items-end' : 'items-start',
                )}
              >
                {/* Name Label */}
                {showAvatar && (
                  <span className="text-[11px] font-medium text-muted-foreground ml-2 mb-1">
                    {resolveUserName(msg.sender_id)}
                  </span>
                )}

                {/* Bubble */}
                <div
                  className={cn(
                    'px-4 py-2.5 text-[15px] leading-relaxed',
                    isMine
                      ? isRead
                        ? 'bg-emerald-600 text-white rounded-2xl rounded-br-[4px] shadow-glass-md shadow-emerald-600/20'
                        : 'bg-primary text-primary-foreground rounded-2xl rounded-br-[4px] shadow-glass-md shadow-primary/20'
                      : 'bg-primary/5 text-foreground rounded-2xl rounded-bl-[4px] border border-primary/10 backdrop-blur-md shadow-glass-sm',
                  )}
                >
                  {msg.body && <p className="whitespace-pre-wrap break-words">{msg.body}</p>}
                  {msg.attachments?.map((attachment) => (
                    <ChatAttachmentItem key={attachment.id} attachment={attachment} />
                  ))}
                </div>

                {/* Meta details (Time + Read receipt) */}
                <div
                  className={cn(
                    'flex items-center gap-1 mt-1 px-1',
                    isMine ? 'justify-end' : 'justify-start ml-2',
                  )}
                >
                  <span className="text-[10px] text-muted-foreground/70 font-medium">
                    {formatDate(msg.sent_at ?? msg.created_at, 'h:mm a')}
                  </span>

                  {isMine && (
                    <span
                      className={cn(
                        'ml-0.5',
                        isRead ? 'text-emerald-600' : 'text-muted-foreground',
                      )}
                    >
                      {isRead ? (
                        <CheckCheck className="h-3.5 w-3.5" />
                      ) : (
                        <Check className="h-3 w-3 text-muted-foreground/50" />
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} className="h-1" />
      </div>
    </ScrollArea>
  );
}
